// Copyright (c) 2018 Deere & Company

import './open-pips-programs-report.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import InlineAlertBar from '../../../shared/components/inline-alert-bar/index';
import Metric from '../state/metric';
import OpenPipPrograms from '@deere/cps-it.warranty-model-factory/lib/open-pip-programs';
import OpenPipProgramsReportCriteria from './open-pip-programs-report-criteria';
import ReportContainer from './open-pip-programs-report-container';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import pipStatuses from '../state/pip-statuses';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleCreateReportClick() {

    this.actions.toggleReportIsScrollable();
    this.actions.resetSearchResults();

    const reportCriteria = {
        accounts: this.selectedDealers,
        includeFullyComplete: this.includeFullyComplete,
        metric: this.metric.selectedMetrics,
        pipNumber: this.pipNumber,
        pipStatuses: this.pipStatuses.selected
    };

    this.actions.createPipProgramsReport(reportCriteria);

}

function handleCriteriaReset() {

    this.actions.selectAllDealersOnLoad(this.selectedAccounts);
    this.actions.resetSearchResults();
    this.actions.updateShowNoResultsFound(false);
    this.actions.resetPipStatuses(this.pipStatuses.allowed);
    this.actions.resetMetrics(this.metric.allowedMetrics);
    this.actions.updateIncludeFullyComplete(false);
    this.actions.updatePipNumber('');

}

function handleDealerSelect(dealer, isSelected) {

    this.actions.selectOpenPipsReportDealer(dealer, isSelected);

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function handleMetricSelect(metric, isSelected) {

    this.actions.selectMetric(metric, isSelected);

}

function handleHideReportColumn(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handlePipNumberChange(event) {

    this.actions.updatePipNumber(event.target.value.toUpperCase());

}

function handleIncludeFullyCompleteChange(isSelected) {

    this.actions.updateIncludeFullyComplete(isSelected);

}

function handlePipStatusSelect(status, isSelected) {

    this.actions.selectPipStatuses(status, isSelected);

}

function handleSelectAllDealers() {

    this.actions.selectAllOpenPipsDealers(this.selectedAccounts);

}

function handleSelectAllMetrics() {

    this.actions.selectAllPipMetrics();

}

function handleSelectAllPipStatuses() {

    this.actions.selectAllPipStatuses();

}

class OpenPipProgramsReport extends Component {

    componentWillMount() {

        this.props.actions.readMetrics();
        this.props.actions.readPipStatuses();
        this.props.actions.selectAllDealersOnLoad(this.props.selectedAccounts);
        this.props.actions.getReportLayouts('open-pip-programs');

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();
            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetSearchResults();
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetPipStatuses(this.props.pipStatuses.allowed);
        this.props.actions.resetReportVariant();

    }

    render() {

        const canRunReport = this.props.selectedDealers.length > 0 && this.props.metric.selectedMetrics.length > 0;

        const reportVariant = {
            isInitialLoad: this.props.isInitialLoad,
            isReportColumnsVisible: this.props.isReportColumnsVisible,
            layouts: this.props.layouts,
            selectedLayout: this.props.selectedLayout
        };

        return (
            <section className='open-pips-report'>
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                <h2 className='open-pips-title'>
                    <FormattedMessage id='OPEN_PIP_PROGRAMS'/>
                    <FavoriteIcon
                        id='OPEN_PIP_PROGRAMS'
                        route={this.props.pathName}
                    />
                </h2>
                <OpenPipProgramsReportCriteria
                    canRunReport={canRunReport}
                    includeFullyComplete={this.props.includeFullyComplete}
                    metric={this.props.metric}
                    onCreateReport={this.props::handleCreateReportClick}
                    onCriteriaReset={this.props::handleCriteriaReset}
                    onDealerSelect={this.props::handleDealerSelect}
                    onIncludeFullyCompleteInputChange={this.props::handleIncludeFullyCompleteChange}
                    onMetricSelect={this.props::handleMetricSelect}
                    onPipNumberInputChange={this.props::handlePipNumberChange}
                    onPipStatusSelect={this.props::handlePipStatusSelect}
                    onSelectAllDealers={this.props::handleSelectAllDealers}
                    onSelectAllPipMetrics={this.props::handleSelectAllMetrics}
                    onSelectAllPipStatuses={this.props::handleSelectAllPipStatuses}
                    pipNumber={this.props.pipNumber}
                    pipStatuses={this.props.pipStatuses}
                    selectedAccounts={this.props.selectedAccounts}
                    selectedDealers={this.props.selectedDealers}
                />
                <InlineAlertBar label='EXCLUDED_PINS_ARE_NOT_INCLUDED_IN_THIS_REPORT'/>
                {this.props.report.length ?
                    <ReportContainer
                        columnApi={this.props.columnApi}
                        gridApi={this.props.gridApi}
                        hideReportColumn={this.props::handleHideReportColumn}
                        onCreateAgGridApi={this.props::handleGridApiDefinition}
                        report={this.props.report}
                        reportVariant={reportVariant}
                    /> :
                        <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

OpenPipProgramsReport.displayName = 'OpenPipProgramsReport';
OpenPipProgramsReport.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    gridApi: PropTypes.object.isRequired,
    includeFullyComplete: PropTypes.bool.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    metric: PropTypes.instanceOf(Metric).isRequired,
    pathName: PropTypes.string.isRequired,
    pipNumber: PropTypes.string.isRequired,
    pipStatuses: PropTypes.instanceOf(pipStatuses).isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(OpenPipPrograms)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedDealers: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired
};

export default OpenPipProgramsReport;



// WEBPACK FOOTER //
// ./src/reports/open-pip-programs/components/open-pip-programs-report.js